import { LOGIN_METHOD } from '@constants'
import { useRecaptcha } from '@cosign-services/hooks/useRecaptcha'
import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { clearLoginOrSignUpMethod, resetSentLoginEvent, setLoginOrSignUpMethod } from '@dni-analysis/events/sendLoginEvent'
import { useEffectOnce } from '@dni-react-hooks/use-effect-once'
import { type Liff } from '@line/liff'
import { Box } from '@pets-ui/Box/Box'
import { ButtonIcon } from '@pets-ui/Button/ButtonIcon'
import { ArrowLeftLarge } from '@pets-ui/icons'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { isEmail } from '@utils'
import Cookies from 'js-cookie'
import { isValidPhoneNumber, parsePhoneNumber, type CountryCode } from 'libphonenumber-js'
import queryString from 'query-string'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom-v5'
import { type Country } from '../../../types/dataModel/platformSdk'
import { getAccount, resetCosignCookies } from '../../../utils/cookie'
import { getSpecialLoginParams } from '../../../utils/getSpecialLoginParams'
import { getWpUserRegion } from '../../../utils/getWpUserRegion'
import { Login, type LoginCustomizationProps } from './Login'
import { LoginEmail } from './LoginEmail'
import { PasswordLogin } from './PasswordLogin'
import { SpecialLogin } from './SpecialLogin'
import { LoginStep } from './types'
import { LoginContext } from './useContextLogin'
import { ValidationEmail } from './ValidationEmail'
import { ValidationForgetPassword } from './ValidationForgetPassword'
import { ValidationPhone } from './ValidationPhone'

interface LoginFlowProps {
  liff?: Liff
  onBack?: () => void
  loginProps?: LoginCustomizationProps
}

/**
 * cosign 登入流程本體：step 狀態機 + LoginContext + 各步驟畫面。
 * 不含整頁外框（CardLogin）與頁面 chrome（useWpLayout）——那些由 LoginPortal 負責，
 * 因此可被彈窗等情境直接嵌入。
 */
export const LoginFlow = ({ liff, onBack, loginProps }: LoginFlowProps) => {
  const { t } = useTranslation()
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const search = queryString.parse(window.location.search)
  const [email, setEmail] = useState('')
  const [prevStepStack, setPrevStepStack] = useState<LoginStep[]>([LoginStep.VALIDATE_EMAIL])
  const [isEmailDomainRejected, setIsEmailDomainRejected] = useState(false)
  const { isSpecialLogin, orderId } = getSpecialLoginParams()
  const [step, _setStep] = useState(() => {
    // 特規連結同時帶 validate=registerOrLogin 與 order_id，須優先落 LOGIN 才顯示得到 SpecialLogin
    if (isSpecialLogin) return LoginStep.LOGIN
    if (search?.validate === 'registerOrLogin') return LoginStep.VALIDATE_EMAIL
    if (search?.validate === 'forgetPassword') return LoginStep.FORGET_PASSWORD
    return LoginStep.LOGIN
  })
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })
  const location = useLocation()
  const { loadRecaptchaScript } = useRecaptcha({ siteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY })

  /**
   * 提早初始化 recaptcha 的 script 加载
   */
  useEffect(() => {
    loadRecaptchaScript().catch((error) => {
      console.error('Failed to load reCAPTCHA:', error)
    })
  }, [loadRecaptchaScript])

  useEffectOnce(() => {
    sendEvent({
      eventName: 'view login page',
      eventParams: {
        page_title: document.title,
        page_location: location.pathname + location.search,
      },
    })
  })
  const setStep = useCallback((nextStep: LoginStep) => {
    if (nextStep === LoginStep.LOGIN) {
      setPrevStepStack([LoginStep.LOGIN])
      _setStep(LoginStep.LOGIN)
      setPhone('')
      setEmail('')
      setIsEmailDomainRejected(false)
      resetCosignCookies()
    }
    else {
      setPrevStepStack([...prevStepStack, step])
      _setStep(nextStep)
    }
  }, [prevStepStack, step])

  const backStep = () => {
    const backStep = prevStepStack.at(-1)
    setStep(backStep || LoginStep.LOGIN)
    setPrevStepStack(prevStepStack.slice(0, -1))
  }
  const [countryCode, setCountryCode] = useState<Country['countryIsoCode'] | undefined>(getWpUserRegion)

  const validatePhone = () => {
    if (!countryCode) return
    if (!phone) {
      setPhoneError(String(t('login.errorPleaseEnterPhone')))
      sendEvent({ eventName: 'login failed', eventParams: { event_category: 'Login', login_error: 'empty error' } })
      return
    }

    if (!isValidPhoneNumber(phone, countryCode as CountryCode)) {
      setPhoneError(String(t('error.incorrectPhoneNumberFormat')))
      sendEvent({ eventName: 'login failed', eventParams: { event_category: 'Login', login_error: 'incorrect format' } })
      return
    }
    setPhoneError('')
    Cookies.set('account', parsePhoneNumber(phone, countryCode as CountryCode).number)
    Cookies.set('country_code', countryCode)
    setStep(LoginStep.VALIDATE_PHONE)
  }

  const loginContextValue = {
    phone,
    setPhone,
    phoneError,
    setPhoneError,
    step,
    setStep,
    email,
    setEmail,
    validatePhone,
    countryCode,
    setCountryCode,
    backStep,
    isEmailDomainRejected,
    setIsEmailDomainRejected,
  }

  useEffect(() => {
    const handleDeleteAccountSnackbar = () => {
      const search = queryString.parse(window.location.search)
      if (search?.deleteAccount === 'true') {
        openSnackbar({
          message: t('login.deleteAccountSuccess'),
          severity: 'success',
        })
        window.history.replaceState(null, '', window.location.pathname)
      }
    }
    handleDeleteAccountSnackbar()
  }, [t, openSnackbar])

  useEffectOnce(() => {
    resetSentLoginEvent()
  })

  useEffect(function syncLoginMethodByStep() {
    // 回到 LOGIN 落地頁＝未選任何方式，清掉殘留 method，避免退回後殘值被當成當次登入的 method
    if (step === LoginStep.LOGIN) clearLoginOrSignUpMethod()
    if ([LoginStep.LOGIN_EMAIL, LoginStep.VALIDATE_EMAIL].includes(step)) setLoginOrSignUpMethod(LOGIN_METHOD.EMAIL)
    if (step === LoginStep.VALIDATE_PHONE) setLoginOrSignUpMethod(LOGIN_METHOD.SMS)
    if ([LoginStep.PASSWORD_LOGIN, LoginStep.FORGET_PASSWORD].includes(step)) {
      const account = getAccount()
      setLoginOrSignUpMethod(account && isEmail(account) ? LOGIN_METHOD.EMAIL : LOGIN_METHOD.SMS)
    }
  }, [step])

  return (
    <LoginContext.Provider value={loginContextValue}>
      {onBack && step === LoginStep.LOGIN && (
        <Box py={4}>
          <ButtonIcon onClick={onBack}>
            <ArrowLeftLarge color={colors.grey600} />
          </ButtonIcon>
        </Box>
      )}
      {step === LoginStep.LOGIN && (isSpecialLogin
        ? <SpecialLogin orderId={orderId} liff={liff} />
        : <Login liff={liff} {...loginProps} />)}
      {step === LoginStep.VALIDATE_PHONE && <ValidationPhone />}
      {step === LoginStep.LOGIN_EMAIL && <LoginEmail />}
      {step === LoginStep.VALIDATE_EMAIL && <ValidationEmail />}
      {step === LoginStep.PASSWORD_LOGIN && <PasswordLogin />}
      {step === LoginStep.FORGET_PASSWORD && <ValidationForgetPassword />}
    </LoginContext.Provider>
  )
}
